/**
 * Publication Card Component
 * Displays a single publication card with thumbnail, title, and link
 * Uses GraphQL Codegen types for type safety
 */

'use client';

import { buttonVariants } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { ExternalLink } from 'lucide-react';
import Image from 'next/image';
import type { PublicationTeaserType } from '../../types/publication';
import { HighlightText } from '../highlight-text';

interface PublicationCardProps {
  publication: PublicationTeaserType;
  searchTerm?: string;
}

/**
 * Format edition date from date string
 */
function formatEditionDate(dateStr: string | null | undefined): string | null {
  if (!dateStr) {
    return null;
  }

  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date.toLocaleDateString('en-GB', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function PublicationCard({ publication, searchTerm = '' }: PublicationCardProps) {
  const title = publication.title || publication.pubRef || 'Publication';
  const href = publication.pubUrl || undefined;
  const thumbUrl = publication.pubThumbUrl || undefined;
  const thumbAlt = title;
  const ref = publication.pubRef;
  const editionDate = formatEditionDate(publication.pubEditionDate || null);
  const description = publication.body?.summary || publication.body?.value || null;
  
  // Strip HTML tags from description
  const cleanDescription = description?.replace(/<[^>]*>/g, '') || null;
  // Truncate description to 150 characters
  const truncatedDescription = cleanDescription 
    ? cleanDescription.length > 150 
      ? cleanDescription.substring(0, 150) + '...' 
      : cleanDescription
    : null;

  const handleImageClick = () => {
    if (href) {
      window.open(href, '_blank', 'noopener,noreferrer');
    }
  };

  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (href) {
      window.open(href, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Card className="group rounded-sm flex flex-row h-full overflow-hidden border bg-card shadow-sm transition hover:shadow-md focus-within:ring-2 focus-within:ring-ring">
      <div
        className="shrink-0 relative cursor-pointer"
        onClick={handleImageClick}
        role={href ? 'button' : undefined}
        tabIndex={href ? 0 : undefined}
        onKeyDown={e => {
          if (href && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            handleImageClick();
          }
        }}
      >
        {thumbUrl ? (
          <div className="relative w-32 h-42 bg-muted overflow-hidden">
            <Image
              src={thumbUrl}
              alt={thumbAlt}
              fill
              className="object-cover transition-transform duration-300 group-hover:scale-[1.04]"
            />
            {/* Overlay and button on hover */}
            {href && (
              <>
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          onClick={handleButtonClick}
                          className={cn(
                            buttonVariants({ variant: 'outline', size: 'lg' }),
                            'bg-background/95 backdrop-blur-sm hover:bg-background'
                          )}
                        >
                          <ExternalLink className="h-5 w-5" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent className="text-xs max-w-xs">
                        <div className="font-semibold">Open</div>
                        <div>in a new tab</div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="relative w-32 h-full bg-muted flex items-center justify-center text-sm text-muted-foreground">
            No preview
          </div>
        )}
      </div>
      <div className="flex flex-col flex-1 min-w-0">
        <CardHeader className="py-3 pb-2">
          <CardTitle className="text-base font-semibold leading-tight">
            <HighlightText text={title} searchTerm={searchTerm} />
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0 pb-3 space-y-2 flex flex-col flex-1">
          {truncatedDescription && (
            <p className="text-sm text-muted-foreground line-clamp-2">
              <HighlightText text={truncatedDescription} searchTerm={searchTerm} />
            </p>
          )}
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            {ref && (
              <span className="font-medium">
                <HighlightText text={ref} searchTerm={searchTerm} />
              </span>
            )}
            {ref && editionDate && <span>•</span>}
            {editionDate && <span>{editionDate}</span>}
          </div>
          
          {/* View button */}
          {href && (
            <div className="mt-auto pt-2 flex justify-end">
              <a
                href={href}
                target="_blank"
                rel="noreferrer"
                className={buttonVariants({ variant: 'outline', size: 'sm' })}
              >
                View
                <span aria-hidden="true">↗</span>
              </a>
            </div>
          )}
        </CardContent>
      </div>
    </Card>
  );
}
